// src/components/ProtectedRoute.jsx

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useWallet } from '../hooks/useWallet';
import { useAccess } from '../hooks/useAccess';
import { SunsetNotice } from './SunsetNotice';

export const ProtectedRoute = ({ children }) => {
  const { jwtToken, isLoading, isInitializing } = useWallet();
  const { product } = useAccess();
  const location = useLocation();

  // 0. Wind-down gate (KAT-230)
  // When the product is closed we don't care about auth state at all — every
  // protected route resolves to the sunset notice, so bookmarks and shared
  // links land somewhere that explains itself instead of bouncing to "/".
  if (!product) {
    return <SunsetNotice />;
  }

  // 1. Handle the initial loading state
  // While the SignedWalletProvider is checking localStorage for a token or initializing, we don't know the auth status.
  // It's best to show a loading indicator to prevent a flicker effect.
  if (isLoading || isInitializing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <h2>Loading authentication status...</h2>
        </div>
      </div>
    );
  }

  // 2. Check for the authentication token
  // If loading is finished and there's no token, redirect to the login page.
  if (!jwtToken) {
    // We use the `replace` prop to avoid adding the login route to the user's history,
    // so they can't click "back" to get to the protected route.
    // We also pass the original location in the state so we can redirect back after login.
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // 3. If the token exists, render the child component
  // The `children` prop will be the page component you want to protect (e.g., <Dashboard />).
  return children;
};